import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ACCOUNT_FEATURES,
  ACCOUNT_FEATURES_NAMES,
  ACCOUNT_SUBTYPES,
  cancelEdit,
  getAccountTypeName,
  hasFeature,
  isValidAccount,
  save,
  selectors as accountSelectors,
  selectEditedAccountId,
  selectAccountEditOpen,
  selectAccountIsSaving,
  selectCandidateParentAccounts,
  selectIsParentAccount,
} from "../../redux/coa";
import ComboBox from "../ComboBox";
import ModalScreen from "../ModalScreen";

const getAccountLabel = (account) => `${account.code} - ${account.name}`;

const EditAccountForm = (props) => {
  const dispatch = useDispatch();

  const { account } = props;

  const isSaving = useSelector(selectAccountIsSaving);

  const [accountCode, setAccountCode] = useState(account?.code || "");
  const [accountFeatures, setAccountFeatures] = useState(
    account?.features || 0,
  );
  const [accountName, setAccountName] = useState(account?.name || "");
  const [accountType, setAccountType] = useState(account?.type || "");
  const [parentAccountId, setParentAccountId] = useState(
    account?.parentId || null,
  );

  const parentAccount = useSelector((state) =>
    accountSelectors.selectById(state, parentAccountId),
  );

  const candidateParents = useSelector((state) =>
    selectCandidateParentAccounts(state, accountType, account?.id),
  );

  useEffect(() => {
    if (!candidateParents.includes(parentAccount)) {
      setParentAccountId(null);
    }
  }, [candidateParents, parentAccount]);

  const accountToSave = {
    code: accountCode,
    features: accountFeatures,
    name: accountName,
    ...(parentAccountId ? { parentId: parentAccountId } : {}),
    type: accountType,
  };

  const isSystemLocked = hasFeature(
    accountFeatures,
    ACCOUNT_FEATURES.SYSTEM_LOCKED,
  );

  const isParentAccount = useSelector((state) =>
    selectIsParentAccount(state, account?.id),
  );
  const canChangeType = !isSystemLocked && !isParentAccount;

  const isValid = isValidAccount(accountToSave);

  const onCodeChange = (event) => {
    setAccountCode(parseInt(event.target.value));
  };

  const onFeaturesChange = (event) => {
    const { checked, value } = event.target;
    setAccountFeatures(
      checked ? accountFeatures | value : accountFeatures & ~value,
    );
  };

  const onNameChange = (event) => {
    setAccountName(event.target.value);
  };

  const onParentChange = (option) => {
    setParentAccountId(option?.id);
  };

  const onTypeChange = (event) => {
    setAccountType(parseInt(event.target.value));
  };

  const onSave = () => {
    const isEdited =
      account?.code !== accountCode ||
      account?.name !== accountName.trim() ||
      account?.features !== accountFeatures ||
      account?.parentId !== parentAccountId ||
      account?.type !== accountType;

    if (isEdited) {
      dispatch(save(accountToSave));
    } else {
      dispatch(cancelEdit());
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <TextField
          autoFocus
          disabled={!canChangeType}
          fullWidth
          label="Type"
          onChange={onTypeChange}
          required
          select
          value={accountType}
        >
          {Object.keys(ACCOUNT_SUBTYPES).map((id) => (
            <MenuItem key={id} value={id}>
              {`${getAccountTypeName(id)} - ${ACCOUNT_SUBTYPES[id].name}`}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid xs={12}>
        <TextField
          fullWidth
          label="Code"
          onChange={onCodeChange}
          required
          type="number"
          value={accountCode}
        />
      </Grid>
      <Grid xs={12}>
        <TextField
          fullWidth
          label="Name"
          onChange={onNameChange}
          required
          value={accountName}
        />
      </Grid>
      <Grid xs={12}>
        <ComboBox
          fullWidth
          getOptionLabel={getAccountLabel}
          label="Parent Account"
          onChange={onParentChange}
          options={candidateParents}
          value={parentAccount || null}
          renderOption={(props, option) => (
            <li {...{ ...props, key: `parentAccountId-${option.id}` }}>
              {getAccountLabel(option)}
            </li>
          )}
        />
      </Grid>
      <Grid xs={12}>
        <FormControl
          required
          component="fieldset"
          disabled={isSystemLocked}
          variant="standard"
        >
          <FormLabel component="legend">Features</FormLabel>
          <FormGroup onChange={onFeaturesChange}>
            {Object.keys(ACCOUNT_FEATURES_NAMES).map((value) => (
              <FormControlLabel
                key={`accountFeature-${value}`}
                checked={hasFeature(accountFeatures, parseInt(value))}
                control={<Checkbox />}
                disabled={
                  parseInt(value) === ACCOUNT_FEATURES.SYSTEM_LOCKED ||
                  isSystemLocked ||
                  (parseInt(value) === ACCOUNT_FEATURES.NO_POSTING &&
                    isParentAccount)
                }
                label={ACCOUNT_FEATURES_NAMES[value]}
                value={parseInt(value)}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Grid>
      <Grid xs={12}>
        <Button
          className={isSaving ? "InProgress" : ""}
          fullWidth
          disabled={!isValid || isSaving}
          onClick={onSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Grid>
    </Grid>
  );
};

const EditAccount = (props) => {
  const dispatch = useDispatch();
  const onClose = () => dispatch(cancelEdit());
  const open = useSelector(selectAccountEditOpen);
  const account = useSelector((state) =>
    accountSelectors.selectById(state, selectEditedAccountId(state)),
  );
  const title = account ? "Edit Account" : "Add New Account";

  return (
    <ModalScreen open={open} onClose={onClose} title={title}>
      <EditAccountForm onClose={onClose} account={account} />
    </ModalScreen>
  );
};

export default EditAccount;
